import React, { useMemo, useState } from 'react';
import ListGrid from '../../../components/ListGrid';
import EmptyScreen from '../../../components/EmptyScreen';
import Dialog from '../../../components/Dialog';
import { IconTrash } from '../../../components/dialogIcons';
import { cellInputStyle, CELL_CLASS } from './movementUi';

/*
  Movement, Account Mappings. Built to Group G's approved drawing on 8 September
  2026.

  -- What this screen is for, which is easy to get wrong --------------------

  **The map does the work, not Claude.** The owner described it as *"the claude
  api takes the short hand they write and then maps it to the correct warehouse
  name in the system"*, and the first half is right: Claude reads "NRMC OR" off
  the handwriting. **The second half is this list.** Turning "NRMC OR" into
  `LOU-NRMC - OR` is a plain lookup in these rows, and Claude is deliberately
  never asked to do it, which is why a page costs about three cents to read.

  So without this list OnHand knows the words a rep wrote and nothing at all
  about where they go. **It is the opposite of optional.**

  -- What changed from what was here ---------------------------------------

  - **A search, and a filter by location.** There was neither. Fine at 31 rows
    and useless at 300, and the map only ever grows: every page read adds any
    shorthand nobody has answered yet.
  - **A shorthand that is a second spelling of a place already mapped says so.**
    Eleven of MedEx's thirty-one are: "St. Patrick E.R.", "St. Patrick ER" and
    "St. Patrick GR" are three rows pointing at one hospital. Without this the
    list reads as thirty-one rows with duplicates in it; with it, it reads as a
    list visibly doing its job.
  - **The count says "31 for 19 places"**, not "31 mappings", for the same
    reason.
  - **Clearing the map is a dialog that says what it costs**, not a grey box the
    browser draws. Thirty-one answers somebody already gave, and every page read
    afterwards asking again from scratch.
  - **The words are the owner's**: brought in from a file, saved as a copy,
    cleared, added. They were Import JSON, Export JSON, Clear all, + Add row.
*/

export default function MappingsTab({ mappings, setMappings, warehouses, status = {}, toast, setBar }) {
  const [search, setSearch] = useState('');
  const [place, setPlace] = useState('all');
  const [asking, setAsking] = useState(false);

  const keys = useMemo(
    () => Object.keys(mappings).sort((a, b) => a.localeCompare(b)),
    [mappings],
  );

  /*
    **How many other ways each place is written.** The whole argument for the
    screen: three rows pointing at one hospital is not a mess, it is three
    spellings a rep actually uses, and saying so turns a duplicate into a fact.
  */
  const spellings = useMemo(() => {
    const byPlace = {};
    for (const k of keys) {
      const w = mappings[k];
      if (!w) continue;
      byPlace[w] = (byPlace[w] || 0) + 1;
    }
    return byPlace;
  }, [keys, mappings]);

  const places = useMemo(
    () => [...new Set(Object.values(mappings).filter(Boolean))].sort(),
    [mappings],
  );

  const shown = useMemo(() => {
    const q = search.trim().toUpperCase();
    return keys.filter(k => {
      if (place !== 'all' && mappings[k] !== place) return false;
      if (!q) return true;
      return k.toUpperCase().includes(q) || (mappings[k] || '').toUpperCase().includes(q);
    });
  }, [keys, mappings, search, place]);

  function renameKey(oldKey, newKey) {
    if (newKey === oldKey) return;
    setMappings(prev => {
      const next = {};
      for (const k of Object.keys(prev)) {
        if (k === oldKey) { if (newKey) next[newKey] = prev[k]; }
        else next[k] = prev[k];
      }
      return next;
    });
  }

  function saveACopy() {
    const blob = new Blob([JSON.stringify(mappings, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'onhand_account_mappings.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function bringSomeIn(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const obj = JSON.parse(await file.text());
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        throw new Error('that file is not a list of shorthands and where they go');
      }
      setMappings(prev => ({ ...prev, ...obj }));
      const n = Object.keys(obj).length;
      toast.success(n + (n === 1 ? ' shorthand' : ' shorthands') + ' brought in.');
    } catch (err) {
      toast.error('Nothing was brought in: ' + err.message);
    }
    e.target.value = '';
  }

  // The bar's own row, handed up so there is still one bar (rule 26).
  React.useEffect(() => {
    if (!setBar) return;
    setBar({
      meta: keys.length
        ? keys.length + ' for ' + places.length + (places.length === 1 ? ' place' : ' places')
        : 'None yet',
      search: keys.length
        ? { value: search, onChange: setSearch, placeholder: 'Search a shorthand or a location' }
        : null,
      /* The location filter belongs in the bar, which is where the approved
         drawing has it. It was a lone select floating above the list. */
      filterCount: place !== 'all' ? 1 : 0,
      drawer: places.length > 1 ? (
        <>
          <span className="tb-lbl">Location</span>
          <select className={'tb-select' + (place !== 'all' ? ' set' : '')}
            value={place} onChange={e => setPlace(e.target.value)}>
            <option value="all">All locations</option>
            {places.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <span style={{ flex: 1 }} />
          {place !== 'all' && (
            <button className="tb-btn tb-btn-danger" onClick={() => setPlace('all')}>Clear filters</button>
          )}
        </>
      ) : null,
      /*
        **Three of the four acts fold behind the "..." below 1024.** They took a
        row each on a phone, so the bar was three rows deep before the list
        started, and Group G's `mappings-390.png` has one row. Adding one is the
        thing you came here to do, so it stays out; bringing a file in, saving a
        copy and clearing the lot are not.
      */
      quiet: (
        <>
          <label className="tb-btn tb-btn-ghost" style={{ cursor: 'pointer', margin: 0 }}>
            Bring some in from a file
            <input type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={bringSomeIn} />
          </label>
          {keys.length > 0 && (
            <>
              <button className="tb-btn tb-btn-ghost" onClick={saveACopy}>Save a copy</button>
              <button className="tb-btn tb-btn-ghost" onClick={() => setAsking(true)}>Clear them all</button>
            </>
          )}
        </>
      ),
      actions: (
        <button className="tb-btn tb-btn-primary"
          onClick={() => setMappings(prev => ({ ...prev, '': warehouses[0] || '' }))}>
          + Add one
        </button>
      ),
    });
  }, [keys.length, places.length, search, place, setBar]); // eslint-disable-line

  const clearing = (
    <Dialog
      kind="danger"
      icon={<IconTrash />}
      title="Clear every mapping?"
      record={keys.length + (keys.length === 1 ? ' shorthand, ' : ' shorthands, ')
        + places.length + (places.length === 1 ? ' place' : ' places')}
      onClose={() => setAsking(false)}
      footer={
        <>
          <button className="btn btn-quiet" onClick={() => setAsking(false)}>Keep them</button>
          <button className="btn btn-danger" onClick={() => { setMappings({}); setAsking(false); }}>
            Clear all {keys.length}
          </button>
        </>
      }
    >
      {/*
        **What it costs, which a grey browser box cannot say.** These are answers
        somebody already gave, one page at a time, and there is no way back.
      */}
      <div className="mv-note held" style={{ marginBottom: 0 }}>
        <div className="mv-note-title">This cannot be undone</div>
        <div className="mv-note-says">
          All {keys.length} mappings go, for everybody, and the next pages you read will ask about
          every shorthand again from scratch.
        </div>
      </div>
    </Dialog>
  );

  if (!keys.length) {
    return (
      <>
        <EmptyScreen
          title="No shorthand mapped yet"
          says={'A rep writes "NRMC OR" on a page and OnHand has to know that means LOU-NRMC - OR. '
              + 'Each one you answer while reading pages is kept, so it is asked once and never again.'}
          action={{
            label: '+ Add one',
            onClick: () => setMappings(prev => ({ ...prev, '': warehouses[0] || '' })),
          }}
        />
        {status.error && (
          <div className="mv-note held">
            <div className="mv-note-title">The shared map could not be read</div>
            <div className="mv-note-says">
              This browser's own copy is being used instead, so anything answered here may not
              reach a colleague. Try again in a moment.
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div>
      {status.error && (
        <div className="mv-note held">
          <div className="mv-note-title">The shared map could not be read</div>
          <div className="mv-note-says">
            This browser's own copy is being used instead, so anything answered here may not
            reach a colleague. Try again in a moment.
          </div>
        </div>
      )}

      {status.saving && (
        <div className="mv-map-filter">
          <span className="mv-setup-note" style={{ marginTop: 0 }}>Saving...</span>
        </div>
      )}

      <div className="rplist">
        {shown.length === 0 ? (
          <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
            Nothing matches what you are looking for.
          </div>
        ) : (
          <ListGrid
            columns={[
              { label: 'Written on the page', width: 'minmax(0,1fr)' },
              { label: 'Goes to', width: 'minmax(0,1.6fr)' },
              /* Two blank headings need keys of their own, or they collide.
                 See the check in ListGrid. */
              { key: 'spellings', label: '', width: 'minmax(0,1fr)' },
              { key: 'delete', label: '', width: '110px', align: 'right' },
            ]}
            rows={shown}
            keyOf={k => k}
            cells={k => [
              <input
                className={CELL_CLASS} style={{ ...cellInputStyle, fontWeight: 700 }} defaultValue={k}
                onBlur={e => renameKey(k, e.target.value.trim())}
              />,
              /*
                **A destination that is no longer an active location is still
                shown.** Found by photographing this screen with MedEx's real
                thirty-one mappings against the test bench's invented
                warehouses: every row read "Nowhere yet", and touching anything
                else on the row would have quietly wiped a good mapping. Rule 9:
                a mapping that points somewhere retired and one that points
                nowhere are two different things.
              */
              <select
                className={CELL_CLASS} style={cellInputStyle} value={mappings[k] || ''}
                onChange={e => setMappings(prev => ({ ...prev, [k]: e.target.value }))}
              >
                <option value="">Nowhere yet</option>
                {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                {mappings[k] && !warehouses.includes(mappings[k]) && (
                  <option value={mappings[k]}>{mappings[k]} (not an active location)</option>
                )}
              </select>,
              spellings[mappings[k]] > 1
                ? <span className="mv-also">
                    Also written {spellings[mappings[k]] - 1}{' '}
                    {spellings[mappings[k]] - 1 === 1 ? 'other way' : 'other ways'}
                  </span>
                : <span />,
              <button className="tbl-btn tbl-btn-danger"
                onClick={() => setMappings(prev => { const next = { ...prev }; delete next[k]; return next; })}>
                Delete
              </button>,
            ]}
            stack={k => (
              <span>
                <span style={{ display: 'block', fontWeight: 800, fontSize: 13 }}>{k}</span>
                <select
                  className={CELL_CLASS} style={{ ...cellInputStyle, marginTop: 8 }} value={mappings[k] || ''}
                  onChange={e => setMappings(prev => ({ ...prev, [k]: e.target.value }))}
                >
                  <option value="">Nowhere yet</option>
                  {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                  {mappings[k] && !warehouses.includes(mappings[k]) && (
                    <option value={mappings[k]}>{mappings[k]} (not an active location)</option>
                  )}
                </select>
                {spellings[mappings[k]] > 1 && (
                  <span className="mv-also" style={{ display: 'inline-block', marginTop: 8 }}>
                    Also written {spellings[mappings[k]] - 1}{' '}
                    {spellings[mappings[k]] - 1 === 1 ? 'other way' : 'other ways'}
                  </span>
                )}
              </span>
            )}
          />
        )}
      </div>

      {asking && clearing}
    </div>
  );
}
